import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/modal";
import Button from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Close } from "@/components/icons";
import "./index.scss";

export interface InstructorItem {
  id: string;
  name: string;
  tcKimlikNo: string;
}

// Mock data – replace with API call when backend is ready
const MOCK_INSTRUCTORS: InstructorItem[] = [
  { id: "1", name: "Elif Demir", tcKimlikNo: "13419435434" },
  { id: "2", name: "Mehmet Yılmaz", tcKimlikNo: "13419435434" },
  { id: "3", name: "Cem Öztürk", tcKimlikNo: "13419435434" },
  { id: "4", name: "Ayşe Kaya", tcKimlikNo: "13419435434" },
  { id: "5", name: "Serkan Akın", tcKimlikNo: "13419435434" },
  { id: "6", name: "Zeynep Tunç", tcKimlikNo: "13419435434" },
  { id: "7", name: "Hasan Güneş", tcKimlikNo: "13419435434" },
  { id: "8", name: "İrem Korkmaz", tcKimlikNo: "13419435434" },
  { id: "9", name: "Fırat Çelik", tcKimlikNo: "13419435434" },
  { id: "10", name: "Deniz Ata", tcKimlikNo: "13419435434" },
];

interface InstructorSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (instructors: InstructorItem[]) => void;
  selectedInstructors: InstructorItem[];
  /** Backend'den gelen eğitmen listesi (yoksa mock kullanılır) */
  instructorsList?: InstructorItem[];
}

const InstructorSelectModal: React.FC<InstructorSelectModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedInstructors: initialSelected,
  instructorsList,
}) => {
  const { t } = useTranslation();
  const list = instructorsList ?? MOCK_INSTRUCTORS;

  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(initialSelected.map((i) => i.id)),
  );

  // Sync local selection when modal opens with new initialSelected
  React.useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set(initialSelected.map((i) => i.id)));
    }
  }, [isOpen, initialSelected]);

  const allSelected =
    list.length > 0 &&
    list.every((i) => selectedIds.has(i.id));

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(list.map((i) => i.id)));
    }
  };

  const selectedList = useMemo(
    () => list.filter((i) => selectedIds.has(i.id)),
    [list, selectedIds],
  );

  const removeSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(selectedList);
    onClose();
  };

  const customFooter = (
    <div className="instructor-select-modal-footer">
      <Button variant="outlined" color="default" onClick={onClose}>
        {t("createEducation.instructorModal.cancel")}
      </Button>
      <Button variant="contained" color="primary" onClick={handleConfirm}>
        {t("createEducation.instructorModal.confirm")}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("createEducation.instructorModal.title")}
      size="lg"
      customFooter={customFooter}
    >
      <div className="instructor-select-modal-content">
        <div className="instructor-select-modal-table-wrap">
          <table className="instructor-select-modal-table">
            <thead>
              <tr>
                <th className="instructor-select-modal-th-check">
                  <Checkbox
                    value={allSelected}
                    onChange={() => toggleSelectAll()}
                    label=""
                  />
                </th>
                <th className="instructor-select-modal-th-name">
                  {t("createEducation.instructorModal.personnelName")}
                </th>
                <th className="instructor-select-modal-th-tc">
                  {t("createEducation.instructorModal.tcId")}
                </th>
              </tr>
            </thead>
            <tbody>
              {list.map((instructor) => (
                <tr key={instructor.id}>
                  <td className="instructor-select-modal-td-check">
                    <Checkbox
                      value={selectedIds.has(instructor.id)}
                      onChange={() => toggleOne(instructor.id)}
                      label=""
                    />
                  </td>
                  <td className="instructor-select-modal-td-name">
                    {instructor.name}
                  </td>
                  <td className="instructor-select-modal-td-tc">
                    {instructor.tcKimlikNo}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selectedList.length > 0 && (
          <div className="instructor-select-modal-selected">
            <span className="instructor-select-modal-selected-label">
              {t("createEducation.instructorModal.selected")}
              :
            </span>
            <div className="instructor-select-modal-tags">
              {selectedList.map((inst) => (
                <span key={inst.id} className="instructor-select-modal-tag">
                  {inst.name}
                  <button
                    type="button"
                    className="instructor-select-modal-tag-remove"
                    onClick={() => removeSelected(inst.id)}
                    aria-label={t("common.remove")}
                  >
                    <Close width={16} height={16} color="#707A8F" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default InstructorSelectModal;
